/**
 * File Utilities for Safe Upload Handling
 * 
 * These utilities ensure file uploads are safe and compatible with
 * Azure Front Door WAF rules (CRS - Core Rule Set).
 * 
 * Problem solved: WAF blocks uploads when filename contains path traversal
 * patterns (../, C:\, /home/user/, etc.) in the multipart Content-Disposition header.
 */

/**
 * Extracts the basename from a file path/name
 * Handles both Windows (C:\path\file.ext) and Unix (/path/file.ext) paths
 * 
 * @param filename - The original filename that may contain a path
 * @returns The basename without any directory components
 */
export function getBasename(filename: string): string {
  if (!filename) return 'file';
  
  // Handle both Windows backslash and Unix forward slash
  // Also handles mixed paths (edge cases)
  const parts = filename.split(/[\\/]/);
  const basename = parts[parts.length - 1] || 'file';
  
  return basename;
}

/**
 * Sanitizes a filename to be safe for upload through Azure Front Door WAF
 * 
 * This function:
 * 1. Extracts only the basename (removes any path components)
 * 2. Removes path traversal patterns (../, ..\)
 * 3. Removes URL-encoded path traversal (%2f, %5c, %2e)
 * 4. Ensures simple UTF-8 compatible characters
 * 5. Preserves the file extension
 * 
 * @param filename - The original filename from the File object
 * @returns A sanitized filename safe for WAF
 */
export function sanitizeFileName(filename: string): string {
  if (!filename) return 'document';
  
  // Step 1: Extract basename only (removes any path)
  let safeName = getBasename(filename);
  
  // Step 2: Remove any remaining path traversal patterns
  // These patterns trigger WAF CRS rules for Path Traversal Attack
  safeName = safeName
    .replace(/\.\.\//g, '')      // Remove ../
    .replace(/\.\.\\/g, '')      // Remove ..\
    .replace(/\.\./g, '')        // Remove .. (double dots)
    .replace(/%2e%2e/gi, '')     // Remove URL-encoded ..
    .replace(/%2f/gi, '')        // Remove URL-encoded /
    .replace(/%5c/gi, '')        // Remove URL-encoded \
    .replace(/%252f/gi, '')      // Remove double URL-encoded /
    .replace(/%255c/gi, '');     // Remove double URL-encoded \
  
  // Step 3: Remove any remaining slashes that might have slipped through
  safeName = safeName.replace(/[/\\]/g, '_');
  
  // Step 4: Remove null bytes and other dangerous characters
  safeName = safeName
    .replace(/\x00/g, '')        // Null byte
    .replace(/%00/gi, '')        // URL-encoded null byte
    .replace(/[<>:"|?*]/g, '_'); // Windows reserved characters
  
  // Step 5: Ensure the filename is not empty after sanitization
  if (!safeName || safeName.trim() === '') {
    return 'document';
  }
  
  // Step 6: Limit filename length (some systems have issues with very long names)
  if (safeName.length > 255) {
    const ext = safeName.lastIndexOf('.');
    if (ext > 0) {
      const extension = safeName.slice(ext);
      const name = safeName.slice(0, 255 - extension.length);
      safeName = name + extension;
    } else {
      safeName = safeName.slice(0, 255);
    }
  }
  
  return safeName.trim();
}

/**
 * Creates a safe FormData append for file uploads
 * 
 * This ensures the filename in the Content-Disposition header
 * is sanitized and won't trigger WAF rules.
 * 
 * Usage:
 *   const formData = new FormData();
 *   appendSafeFile(formData, 'file', file);
 * 
 * @param formData - The FormData object to append to
 * @param fieldName - The form field name (usually 'file')
 * @param file - The File object to append
 */
export function appendSafeFile(
  formData: FormData,
  fieldName: string,
  file: File
): void {
  const safeFileName = sanitizeFileName(file.name);
  
  // FormData.append() third parameter overrides the filename
  // in the Content-Disposition header
  formData.append(fieldName, file, safeFileName);
}

/**
 * Validates if a filename is safe for upload (doesn't contain dangerous patterns)
 * Useful for pre-validation before upload attempt
 * 
 * @param filename - The filename to validate
 * @returns true if the filename appears safe, false if it contains suspicious patterns
 */
export function isFileNameSafe(filename: string): boolean {
  if (!filename) return false;
  
  const dangerousPatterns = [
    /\.\.\//,           // Path traversal ../
    /\.\.\\/,           // Path traversal ..\
    /%2e%2e/i,          // URL-encoded ..
    /%2f/i,             // URL-encoded /
    /%5c/i,             // URL-encoded \
    /^[a-zA-Z]:\\/,     // Windows absolute path (C:\)
    /^\//,              // Unix absolute path
    /\x00/,             // Null byte
    /%00/i,             // URL-encoded null byte
  ];
  
  return !dangerousPatterns.some(pattern => pattern.test(filename));
}

